import * as React from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { throttle } from 'throttle-debounce';

import { changeBounds } from '../../../../common/actions/mapBounds';
import { useApplicationContext } from '../../../../common/containers/ApplicationContext';
import { TThunkDispatch } from '../../../../common/types/redux';
import { isCenterZoomBounds } from '../../../../common/utils/mapBounds';
import { zoomToPrecision } from '../../../../common/utils/precision';
import { useMapController } from '../../../controller/useMapController';
import { selectMapBounds } from '../../../selectors';
import { TMapBounds } from '../../../types/mapBounds';

interface IUseMapBoundsSyncParams {
  map: YMaps.Map;
  /** Вызывается, когда программный фокус карты (setBounds/setCenter) реально применился. */
  onFocusApplied?: () => void;
}

const INITIAL_FOCUS_OPTIONS: YMaps.ISetCenterOptions = { duration: 0 };

export function useBoundsSync({ map, onFocusApplied }: IUseMapBoundsSyncParams): void {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const controller = useMapController();
  const storeMapBounds = useSelector(selectMapBounds);
  const [currentMapBounds, setCurrentMapBounds] = React.useState<TMapBounds>();
  const isInitialStoreSyncRef = React.useRef(true);
  const shouldWaitForInitialFocusRef = React.useRef(isCenterZoomBounds(storeMapBounds));

  const handleBoundsChange = React.useMemo(
    () =>
      throttle(300, () => {
        const zoom = map.getZoom();
        const visibleBounds = map.getBounds({ useMapMargin: true });

        if (controller.getCorrectedVisibleBounds(visibleBounds)) {
          return;
        }

        const nextBounds: TMapBounds = {
          // useMapMargin: bbox видимой части карты, без полосы под desktop listing.
          // Отсюда redux mapBounds питает запросы пинов/выдачи (первый fetch, теги, сортировка,
          // гео) - они идут строго по тому, что пользователь видит, а не по скрытой под листингом зоне.
          bounds: visibleBounds,
          center: map.getCenter({ useMapMargin: true }),
          precision: zoomToPrecision(config, zoom),
          zoom,
        };

        // Нужно уносить в следующую макротаску, иначе насыпает ошибки в консоли о том,
        // что flushSync работает некорректно, ведь в текущей таске уже происходит рендеринг,
        // поэтому требуется уносить рендеринг при помощи flushSync в следующую таску.
        setTimeout(() => {
          // Если flushSync убрать, то зацикливается получение кластеров (после перехода на React 18)
          flushSync(() => {
            setCurrentMapBounds(nextBounds);
            dispatch(changeBounds(nextBounds));
          });
        }, 0);
      }),
    [config, controller, dispatch, map],
  );

  React.useEffect(() => {
    map.events.add('boundschange', handleBoundsChange);

    const handleInitialCorrectionApplied = () => {
      if (!shouldWaitForInitialFocusRef.current) {
        handleBoundsChange();
      }
    };

    const isVisibleRestrictCorrectionStarted =
      controller.correctVisibleAreaToRestrictArea(handleInitialCorrectionApplied);

    if (!isVisibleRestrictCorrectionStarted && !shouldWaitForInitialFocusRef.current) {
      handleBoundsChange();
    }

    return () => {
      map.events.remove('boundschange', handleBoundsChange);
    };
  }, [map, controller, handleBoundsChange]);

  React.useEffect(() => {
    const isInitialStoreSync = isInitialStoreSyncRef.current;
    isInitialStoreSyncRef.current = false;

    if (storeMapBounds === currentMapBounds) {
      return;
    }

    // Программный фокус карты на новый вид из стора идёт ЧЕРЕЗ контроллер - единая точка
    // работы с картой: движение помечается непользовательским (не порождает лишних запросов),
    // а useMapMargin в контроллере вписывает целевой вид в видимую часть, согласованно с тем,
    // что handleBoundsChange синкает в стор. onApplied зовётся только при успешном применении
    // (при отказе ymaps, например за restrictMapArea, фаза 2 гео-смены не уйдёт - см. useGeoFocusRefetch).
    const onApplied = () => {
      shouldWaitForInitialFocusRef.current = false;
      handleBoundsChange();
      onFocusApplied?.();
    };

    if (storeMapBounds.bounds) {
      if (isInitialStoreSync) {
        return;
      }

      controller.moveTo({ bounds: storeMapBounds.bounds, onApplied });
    } else if (isCenterZoomBounds(storeMapBounds)) {
      const options = isInitialStoreSync ? INITIAL_FOCUS_OPTIONS : undefined;

      controller.focusOn({ center: storeMapBounds.center, zoom: storeMapBounds.zoom, options, onApplied });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMapBounds]);
}
